import React, {useState} from "react";

import useDailyOrders from "@apps/daily-menu/hooks/useDailyOrders";
import cx from "classnames";
import {useParams} from "react-router-dom";

import {SECONDARY_ERROR_MODAL_CONTENT} from "@globals/constants/error_modal";
import useCutoffTimes from "@globals/hooks/useCutoffTimes";
import useMenuDates from "@globals/hooks/useMenuDates";
import {CheckoutResponse} from "@globals/types/CheckoutResponse";
import {useUser} from "@globals/user";

import DailyCart from "@components/DailyCart";
import Footer from "@components/layout/Footer";
import MaxWidthContainer from "@components/layout/MaxWidthContainer";
import PageContainer from "@components/layout/PageContainer";
import SubHeader from "@components/layout/SubHeader";
import CutoffModal from "@components/modals/CutoffModal";
import ErrorModal from "@components/modals/ErrorModal";

import BrandDishes from "./BrandDishes";
import BrandInfo from "./BrandInfo";
import SeeMenuLink from "./SeeMenuLink/SeeMenuLink";
import UpcomingDishes from "./UpcomingDishes";
import useBrand from "./hooks/useBrand";
import useBrandDishes from "./hooks/useBrandDishes";
import useBrandMenu from "./hooks/useBrandMenu";
import styles from "./index.scss";
import {MenuLinkLabel} from "./interfaces";
import formatError from "./utils/formatError";

function refreshPage() {
  window.location.reload();
}

export default function BrandApp(): JSX.Element {
  const {operatorSlug, restaurantSlug} = useParams();
  const {user, isAuthorized} = useUser();
  const [menuLinkLabel, setMenuLinkLabel] = useState<string>(
    MenuLinkLabel.SeeMenu
  );

  const {
    brand,
    isError: isInfoError,
    isFetching: isInfoFetching,
    error: infoError,
  } = useBrand(operatorSlug, restaurantSlug);

  const {
    brandMenus,
    dailyMenuDate,
    hasNoUpcomingDishes,
    isLoading: isMenusLoading,
    isError: isMenuError,
    error: menuError,
    setNextMenuDate,
  } = useBrandMenu(operatorSlug, brand?.slug);

  const {
    dishes,
    isLoading: isAllDishesLoading,
    isError: isAllDishesError,
    error: allDishesError,
  } = useBrandDishes(operatorSlug, brand?.slug);

  const {menuDates} = useMenuDates({disabled: !isAuthorized});

  const {cutoffTimes} = useCutoffTimes({
    selectedDate: menuDates[0],
    selectedLocation: user?.selected_location.id,
  });

  const {
    addItem,
    partialOrders,
    handleItemUpdate,
    isLoading: isOrdersLoading,
    checkout,
    latestError: orderError,
    resetOrder,
  } = useDailyOrders();

  const showError = isInfoError || isMenuError || isAllDishesError;
  const error = infoError || menuError || allDishesError;
  const showInvalidSlugError =
    !isInfoFetching && isInfoError && infoError?.status === 404;
  const showContent = !showInvalidSlugError;

  const onCheckoutSuccess = (checkoutResponse: CheckoutResponse) => {
    setNextMenuDate(checkoutResponse.next_day);
    setMenuLinkLabel(MenuLinkLabel.SeeNextMenu);
  };

  return (
    <>
      {isAuthorized && (
        <CutoffModal
          cutoffTimes={cutoffTimes}
          confirmButtonText="Refresh"
          onConfirm={refreshPage}
        />
      )}
      <PageContainer withHeader={true}>
        {isAuthorized && (
          <SubHeader className={styles.stickySubheader}>
            <MaxWidthContainer className={styles.container}>
              <div className={styles.subHeaderContainer}>
                <SeeMenuLink
                  linkLabel={menuLinkLabel}
                  dailyMenuDate={dailyMenuDate}
                />
                <DailyCart
                  resetOrder={resetOrder}
                  partialOrders={partialOrders}
                  handleItemUpdate={handleItemUpdate}
                  isLoading={isOrdersLoading}
                  checkout={checkout}
                  onCheckoutSuccess={onCheckoutSuccess}
                />
              </div>
            </MaxWidthContainer>
          </SubHeader>
        )}
        <MaxWidthContainer className={styles.container} tagName="main">
          {showInvalidSlugError && (
            <div className={cx(styles.content, styles.errorMessageContainer)}>
              {formatError(error!)}
            </div>
          )}
          {showContent && (
            <div className={styles.content}>
              <BrandInfo brand={brand} isLoading={isInfoFetching} />
              {showError && (
                <div className={styles.loadingError}>{formatError(error!)}</div>
              )}
              {!isAuthorized || (
                <UpcomingDishes
                  brandMenus={brandMenus}
                  hasNoUpcomingDishes={hasNoUpcomingDishes}
                  addItem={addItem}
                  isLoading={isMenusLoading}
                  isError={isMenuError}
                />
              )}
              {brand && !isAllDishesError && (
                <BrandDishes
                  brand={brand}
                  dishes={dishes}
                  isLoading={isAllDishesLoading}
                />
              )}
            </div>
          )}
          <ErrorModal
            error={orderError}
            defaultContent={SECONDARY_ERROR_MODAL_CONTENT}
          />
        </MaxWidthContainer>
      </PageContainer>
      <Footer />
    </>
  );
}
